import { useState, useEffect } from 'react';
import { api, getErrorMessage } from '../App';
import { toast } from 'sonner';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { 
  Building2, Save, MapPin, Phone, Mail, FileText, 
  Hash, Globe, Loader2, Landmark, Plus, Edit, Trash2
} from 'lucide-react';

export default function CompanySetup({ user }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [company, setCompany] = useState(null);
  const [banks, setBanks] = useState([]);
  const [loadingBanks, setLoadingBanks] = useState(false);
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [editingBank, setEditingBank] = useState(null);
  const [savingBank, setSavingBank] = useState(false);
  const [bankForm, setBankForm] = useState({
    bank_name: '',
    account_name: '',
    account_number: '',
    ifsc_code: '',
    branch: '',
    opening_balance: 0
  });
  const [formData, setFormData] = useState({
    name: '',
    legal_name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    pan_number: '',
    gstin: '',
    cin: '',
    tan: '',
    website: ''
  });

  useEffect(() => {
    fetchCompany();
  }, [user]);

  const fetchCompany = async () => {
    try {
      setLoading(true);
      
      // Get company ID from user or localStorage
      let companyId = user?.company_id || localStorage.getItem('selectedCompanyId');
      
      if (!companyId && user?.role === 'super_admin') {
        // Super admin needs to select a company first
        const response = await api.get('/companies');
        if (response.data.length > 0) {
          companyId = response.data[0].id;
        }
      }
      
      if (!companyId) {
        toast.error('No company selected');
        setLoading(false);
        return;
      }
      
      const response = await api.get(`/companies/${companyId}`);
      setCompany(response.data);
      setFormData({
        name: response.data.name || '',
        legal_name: response.data.legal_name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        address: response.data.address || '',
        city: response.data.city || '',
        state: response.data.state || '',
        country: response.data.country || 'India',
        pincode: response.data.pincode || '',
        pan_number: response.data.pan_number || '',
        gstin: response.data.gstin || '',
        cin: response.data.cin || '',
        tan: response.data.tan || '',
        website: response.data.website || ''
      });
      
      // Fetch banks for this company
      fetchBanks(companyId);
    } catch (error) {
      toast.error('Failed to fetch company details');
    } finally {
      setLoading(false);
    }
  };

  const fetchBanks = async (companyId) => {
    setLoadingBanks(true);
    try {
      // Get all ledgers and filter for bank accounts (group code starting with 111 or under Bank Accounts group)
      const response = await api.get('/finance/ledgers', {
        params: { company_id: companyId }
      });
      
      // Filter ledgers that are bank accounts (code starting with 1112 or name contains 'bank')
      const bankLedgers = response.data.filter(ledger => 
        ledger.code?.startsWith('1112') || 
        ledger.account_group_name?.toLowerCase().includes('bank') ||
        ledger.name?.toLowerCase().includes('bank')
      );
      
      setBanks(bankLedgers);
    } catch (error) {
      console.error('Failed to fetch banks:', error);
    } finally {
      setLoadingBanks(false);
    }
  };

  const openBankDialog = (bank = null) => {
    if (bank) {
      setEditingBank(bank);
      setBankForm({
        bank_name: bank.bank_name || bank.name?.replace(' - Bank A/c', '') || '',
        account_name: bank.account_name || bank.name || '',
        account_number: bank.account_number || '',
        ifsc_code: bank.ifsc_code || '',
        branch: bank.branch || '',
        opening_balance: bank.opening_balance || 0
      });
    } else {
      setEditingBank(null);
      setBankForm({
        bank_name: '',
        account_name: '',
        account_number: '',
        ifsc_code: '',
        branch: '',
        opening_balance: 0
      });
    }
    setBankDialogOpen(true);
  };

  const handleSaveBank = async () => {
    if (!bankForm.bank_name.trim()) {
      toast.error('Bank name is required');
      return;
    }
    if (!bankForm.account_number.trim()) {
      toast.error('Account number is required');
      return;
    }

    setSavingBank(true);
    try {
      // Get the Bank Accounts group
      const groupsRes = await api.get('/finance/account-groups', {
        params: { company_id: company.id }
      });
      
      const bankGroup = groupsRes.data.find(g => 
        g.code === '1112' || 
        g.name?.toLowerCase().includes('bank account') ||
        g.name?.toLowerCase() === 'bank accounts'
      );
      
      if (!bankGroup) {
        toast.error('Bank Accounts group not found. Please set up Finance first.');
        setSavingBank(false);
        return;
      }

      // Generate a unique code for the bank ledger
      const existingBankCodes = banks.map(b => b.code).filter(c => c?.startsWith('1112'));
      const nextNum = existingBankCodes.length + 1;
      const newCode = `1112${String(nextNum).padStart(2, '0')}`;

      const ledgerName = `${bankForm.bank_name} - ${bankForm.account_number.slice(-4)}`;
      
      const ledgerData = {
        name: ledgerName,
        code: editingBank ? editingBank.code : newCode,
        account_group_id: bankGroup.id,
        opening_balance: parseFloat(bankForm.opening_balance) || 0,
        bank_name: bankForm.bank_name,
        account_name: bankForm.account_name,
        account_number: bankForm.account_number,
        ifsc_code: bankForm.ifsc_code,
        branch: bankForm.branch,
        is_active: true
      };

      if (editingBank) {
        await api.put(`/finance/ledgers/${editingBank.id}`, ledgerData);
        toast.success('Bank account updated');
      } else {
        await api.post('/finance/ledgers', ledgerData);
        toast.success('Bank account added');
      }

      setBankDialogOpen(false);
      fetchBanks(company.id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to save bank account'));
    } finally {
      setSavingBank(false);
    }
  };

  const handleDeleteBank = async (bank) => {
    if (!window.confirm(`Are you sure you want to delete ${bank.name}?`)) return;
    
    try {
      await api.delete(`/finance/ledgers/${bank.id}`);
      toast.success('Bank account deleted');
      fetchBanks(company.id);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete bank account'));
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await api.put(`/companies/${company.id}`, formData);
      toast.success('Company details updated successfully');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update company'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-orange-500" />
          <p className="mt-2 text-gray-500">Loading company details...</p>
        </div>
      </div>
    );
  }

  if (!company) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Card className="max-w-md">
          <CardContent className="p-8 text-center">
            <Building2 className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No Company Selected</h3>
            <p className="text-gray-500">Please select a company from the dropdown to manage its settings.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="company-setup">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Building2 className="h-8 w-8 text-orange-500" />
            Company Setup
          </h1>
          <p className="text-gray-600 mt-1">Manage company details and statutory information</p>
        </div>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
          data-testid="save-company-btn"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <Tabs defaultValue="basic" className="w-full">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="basic">Basic Info</TabsTrigger>
          <TabsTrigger value="address">Address</TabsTrigger>
          <TabsTrigger value="statutory">Statutory</TabsTrigger>
          <TabsTrigger value="banks">Banks</TabsTrigger>
        </TabsList>

        {/* Basic Info Tab */}
        <TabsContent value="basic" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-orange-500" />
                Basic Information
              </CardTitle>
              <CardDescription>Company name and contact details</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Company Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter company name"
                    data-testid="company-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="legal_name">Legal Name</Label>
                  <Input
                    id="legal_name"
                    value={formData.legal_name}
                    onChange={(e) => setFormData({ ...formData, legal_name: e.target.value })}
                    placeholder="Registered legal name"
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email *</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="company@example.com"
                      className="pl-10"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="phone">Phone *</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="phone"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="+91 XXXXX XXXXX"
                      className="pl-10"
                    />
                  </div>
                </div>
              </div>
              
              <div>
                <Label htmlFor="website">Website</Label>
                <div className="relative">
                  <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="website"
                    value={formData.website}
                    onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                    placeholder="https://www.example.com"
                    className="pl-10"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Address Tab */}
        <TabsContent value="address" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <MapPin className="h-5 w-5 text-orange-500" />
                Registered Address
              </CardTitle>
              <CardDescription>Company&apos;s registered address for invoicing and legal purposes</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="address">Street Address</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Enter street address"
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="City"
                  />
                </div>
                <div>
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="State"
                  />
                </div>
                <div>
                  <Label htmlFor="pincode">Pincode</Label>
                  <Input
                    id="pincode"
                    value={formData.pincode}
                    onChange={(e) => setFormData({ ...formData, pincode: e.target.value })}
                    placeholder="400001"
                    maxLength={6}
                  />
                </div>
              </div>
              
              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  placeholder="Country"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Statutory Tab */}
        <TabsContent value="statutory" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-orange-500" />
                Statutory Information
              </CardTitle>
              <CardDescription>Tax and legal registration numbers for compliance</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="pan_number">PAN Number</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="pan_number"
                      value={formData.pan_number}
                      onChange={(e) => setFormData({ ...formData, pan_number: e.target.value.toUpperCase() })}
                      placeholder="AAAAA1234A"
                      className="pl-10 font-mono"
                      maxLength={10}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Permanent Account Number</p>
                </div>
                <div>
                  <Label htmlFor="gstin">GSTIN</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="gstin"
                      value={formData.gstin}
                      onChange={(e) => setFormData({ ...formData, gstin: e.target.value.toUpperCase() })}
                      placeholder="22AAAAA0000A1Z5"
                      className="pl-10 font-mono"
                      maxLength={15}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Goods & Services Tax Identification Number</p>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cin">CIN</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="cin"
                      value={formData.cin}
                      onChange={(e) => setFormData({ ...formData, cin: e.target.value.toUpperCase() })}
                      placeholder="U12345MH2020PTC123456"
                      className="pl-10 font-mono"
                      maxLength={21}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Corporate Identification Number (for Pvt Ltd / Ltd companies)</p>
                </div>
                <div>
                  <Label htmlFor="tan">TAN</Label>
                  <div className="relative">
                    <Hash className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      id="tan"
                      value={formData.tan}
                      onChange={(e) => setFormData({ ...formData, tan: e.target.value.toUpperCase() })}
                      placeholder="AAAA12345A"
                      className="pl-10 font-mono"
                      maxLength={10}
                    />
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Tax Deduction Account Number</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Banks Tab */}
        <TabsContent value="banks" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Landmark className="h-5 w-5 text-orange-500" />
                    Bank Accounts
                  </CardTitle>
                  <CardDescription>Manage company bank accounts for financial transactions</CardDescription>
                </div>
                <Button 
                  onClick={() => openBankDialog()}
                  className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
                  data-testid="add-bank-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Bank Account
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingBanks ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
                  <span className="ml-2 text-gray-500">Loading bank accounts...</span>
                </div>
              ) : banks.length === 0 ? (
                <div className="text-center py-8">
                  <Landmark className="h-12 w-12 mx-auto text-gray-300 mb-4" />
                  <p className="text-gray-500">No bank accounts configured</p>
                  <p className="text-sm text-gray-400 mt-1">Add your first bank account to get started</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Bank Name</TableHead>
                      <TableHead>Account Number</TableHead>
                      <TableHead>IFSC Code</TableHead>
                      <TableHead>Branch</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {banks.map((bank) => {
                      const hasTransactions = (bank.current_balance || 0) !== (bank.opening_balance || 0);
                      return (
                        <TableRow key={bank.id} data-testid={`bank-row-${bank.id}`}>
                          <TableCell className="font-medium">{bank.bank_name || bank.name}</TableCell>
                          <TableCell className="font-mono">{bank.account_number || '-'}</TableCell>
                          <TableCell className="font-mono">{bank.ifsc_code || '-'}</TableCell>
                          <TableCell>{bank.branch || '-'}</TableCell>
                          <TableCell className="text-right font-medium">
                            ₹{((bank.current_balance || 0) + (bank.opening_balance || 0)).toLocaleString('en-IN')}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => openBankDialog(bank)}
                                disabled={hasTransactions}
                                title={hasTransactions ? "Cannot edit: Bank has transactions" : "Edit bank"}
                                data-testid={`edit-bank-${bank.id}`}
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleDeleteBank(bank)}
                                disabled={hasTransactions}
                                className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                title={hasTransactions ? "Cannot delete: Bank has transactions" : "Delete bank"}
                                data-testid={`delete-bank-${bank.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add/Edit Bank Dialog */}
      <Dialog open={bankDialogOpen} onOpenChange={setBankDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Landmark className="h-5 w-5 text-orange-500" />
              {editingBank ? 'Edit Bank Account' : 'Add Bank Account'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="bank_name">Bank Name *</Label>
              <Input
                id="bank_name"
                value={bankForm.bank_name}
                onChange={(e) => setBankForm({ ...bankForm, bank_name: e.target.value })}
                placeholder="e.g., HDFC Bank, ICICI Bank"
                data-testid="bank-name-input"
              />
            </div>
            <div>
              <Label htmlFor="account_name">Account Holder Name</Label>
              <Input
                id="account_name"
                value={bankForm.account_name}
                onChange={(e) => setBankForm({ ...bankForm, account_name: e.target.value })}
                placeholder="Name on account"
              />
            </div>
            <div>
              <Label htmlFor="account_number">Account Number *</Label>
              <Input
                id="account_number"
                value={bankForm.account_number}
                onChange={(e) => setBankForm({ ...bankForm, account_number: e.target.value })}
                placeholder="Enter account number"
                className="font-mono"
                data-testid="account-number-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="ifsc_code">IFSC Code</Label>
                <Input
                  id="ifsc_code"
                  value={bankForm.ifsc_code}
                  onChange={(e) => setBankForm({ ...bankForm, ifsc_code: e.target.value.toUpperCase() })}
                  placeholder="HDFC0001234"
                  className="font-mono"
                  maxLength={11}
                />
              </div>
              <div>
                <Label htmlFor="branch">Branch</Label>
                <Input
                  id="branch"
                  value={bankForm.branch}
                  onChange={(e) => setBankForm({ ...bankForm, branch: e.target.value })}
                  placeholder="Branch name"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="opening_balance">Opening Balance (₹)</Label>
              <Input
                id="opening_balance"
                type="number"
                value={bankForm.opening_balance}
                onChange={(e) => setBankForm({ ...bankForm, opening_balance: parseFloat(e.target.value) || 0 })}
                placeholder="0.00"
                data-testid="opening-balance-input"
              />
              <p className="text-xs text-gray-500 mt-1">Initial balance when adding this account</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setBankDialogOpen(false)}>Cancel</Button>
            <Button 
              onClick={handleSaveBank}
              disabled={savingBank}
              className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              data-testid="save-bank-btn"
            >
              {savingBank ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="h-4 w-4 mr-2" />
                  {editingBank ? 'Update' : 'Add'} Bank
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
